<!-- Talent Pool Container with Scrolling -->
<!-- `#scrollContainer` template ref is consumed by the V3 infiniteScroll
     directive below. Avoid `[infiniteScrollContainer]="'.talent-pool-container'"`
     with `[fromRoot]="true"` — both this component and
     `app-suggested-candidates-ai-list` use the same class name, and a tab
     switch can leave the previous instance's container momentarily in the DOM
     when the new directive resolves its target, binding the scroll listener
     to a stale detached element. Passing the element ref directly skips the
     `document.querySelector` step. -->
<div #scrollContainer id="talent-pool-container" class="talent-pool-container">

  <!-- ==================== V3 Design ==================== -->
  <ng-container *ngIf="designVersion === 'v3'">
    <!-- AI Smart Search Banner -->
    <div class="jc-v3-ai-banner">
      <div class="jc-v3-ai-banner-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2a10 10 0 1 0 10 10"/><path d="M12 6v6l4 2"/><path d="M18 2l4 4-4 4"/></svg>
      </div>
      <div class="jc-v3-ai-banner-body">
        <div class="jc-v3-ai-banner-title">
          Results powered by Smart Search
          <span class="jc-v3-ai-chip">
            <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
            AI
          </span>
        </div>
        <div class="jc-v3-ai-banner-sub">Candidates are ranked by skills match, availability, and experience alignment with this job.</div>
      </div>
    </div>

    <!-- Header count cards — wireframe `gigsumo_job_center_v2(16).html`
         line 1274 (`stat-grid stat-grid-3`). Talent-pool pipeline pip-bar
         (All / Sourced / Reached Out / Engaged / Qualified) and the
         search/filter row are intentionally absent from the wireframe and
         have been removed; this stat grid replaces them. -->
    <div class="jc-v3-stat-grid jc-v3-stat-grid-3">
      <div class="jc-v3-stat-card acc-purple">
        <div class="jc-v3-s-label">Talent Pool</div>
        <div class="jc-v3-s-value">{{ talentPoolTotal }}</div>
        <div class="jc-v3-s-sub">Total candidates</div>
      </div>
      <div class="jc-v3-stat-card acc-green">
        <div class="jc-v3-s-label">Available Now</div>
        <div class="jc-v3-s-value">{{ immediateAvailableCount }}</div>
        <div class="jc-v3-s-sub">Immediate availability</div>
      </div>
      <div class="jc-v3-stat-card acc-amber">
        <div class="jc-v3-s-label">Avg Match</div>
        <div class="jc-v3-s-value">{{ averageScore }}%</div>
        <div class="jc-v3-s-sub">Skills match score</div>
      </div>
    </div>

    <!-- V3 Talent Pool List using candidate-card-table-view.
         The actual scroll container is `.talent-pool-container` (this
         component's root, see talent-pool.component.scss — `height: 100%;
         overflow: auto`). The previous setup pointed `[infiniteScrollContainer]`
         at the outer `.jc-v3-entity-details-body`, but that body never scrolls
         here because this inner container claims the overflow first — so
         `(scrolled)` never fired and pagination was silently broken. -->
    <!-- Job-level disable: blocked / non-active job greys the whole
         Talent Pool listing and blocks pointer events. Mirrors the
         job-applicants pattern (job-applicants.component.html:62). -->
    <div infiniteScroll
         [infiniteScrollDistance]="5"
         [infiniteScrollThrottle]="200"
         [infiniteScrollContainer]="scrollContainer"
         [fromRoot]="false"
         [alwaysCallback]="true"
         [ngClass]="{'disabled': !jobDetails?.availability || jobDetails?.status !== 'ACTIVE'}"
         (scrolled)="onScrollDown()">
      <app-candidate-card-table-view
        [designVersion]="'v3'"
        [pageName]="'talent-pool'"
        [response]="response"
        [loadAPIcall]="loadAPIcall"
        [stopscrollFlag]="stopscrollFlag"
        [responseReceived]="responseReceived"
        [jobDetails]="jobDetails"
        [userId]="userId"
        [showTimeline]="false"
        (onCandidateClick)="route($event)"
        (onViewProfile)="open($event)"
        (onDownloadResume)="downloadResume($event)"
        (onSendMessage)="sendMessage($event)"
        (onOpenDocuments)="openDocuments($event)"
        (onShareProfile)="shareProfile($event)"
        (onScheduleInterview)="scheduleInterview($event)"
        (onAddNotes)="addNotes($event)"
        (onStatusUpdate)="updateStatus($event.status, $event.candidate)"
        (showDetails)="showDetails($event,DetailsPopup)"
        (inviteClicked)="invite($event)">
      </app-candidate-card-table-view>

      <!-- Loading skeleton for subsequent pages -->
      <div class="col-12" *ngIf="loadAPIcall && response?.length > 0">
        <app-common-skeleton [countforView]="2" [NameOftheScreen]="'jobcardlarge'"></app-common-skeleton>
      </div>
    </div>
    <!-- AI-processing state — covers the initial findTalentPoolMatches call
         AND its retry attempts (3 × 25s in talentPoolWithRetry): loadAPIcall
         stays true for the whole window. After the last failed retry the
         error handler flips loadAPIcall false → the "No Talent Pool Yet"
         empty state below takes over. Mirrors Marketplace Candidates. -->
    <div *ngIf="loadAPIcall && response?.length === 0" class="jc-v3-empty-state">
      <svg width="200" height="180" viewBox="0 0 200 180" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="100" cy="90" r="80" fill="#F5F3FF"/>
        <circle cx="100" cy="90" r="40" fill="#EDE9FE" stroke="#7C3AED" stroke-width="1.8"/>
        <path d="M100 70 v25 l15 10" stroke="#7C3AED" stroke-width="2.5" stroke-linecap="round" fill="none"/>
        <circle cx="148" cy="58" r="14" fill="#C4B5FD"/>
        <polygon points="148,51 151,57 157,57 152,61 154,67 148,63 142,67 144,61 139,57 145,57" fill="#ffffff"/>
        <circle cx="50" cy="52" r="3" fill="#A78BFA"/>
        <circle cx="155" cy="128" r="2" fill="#C4B5FD"/>
      </svg>
      <h3>AI is analyzing</h3>
      <p>Our AI is analyzing this job and building your talent pool. This may take a moment...</p>
    </div>
    <div *ngIf="!loadAPIcall && response?.length === 0" class="jc-v3-empty-state">
      <svg width="200" height="180" viewBox="0 0 200 180" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="100" cy="90" r="80" fill="#F5F3FF"/>
        <circle cx="86" cy="72" r="18" fill="#EDE9FE" stroke="#7C3AED" stroke-width="1.8"/>
        <circle cx="86" cy="70" r="8" fill="#ffffff" stroke="#7C3AED" stroke-width="1.5"/>
        <path d="M68 104 c0-12 8-20 18-20h0c10 0 18 8 18 20" fill="#EDE9FE" stroke="#7C3AED" stroke-width="1.8" stroke-linecap="round"/>
        <circle cx="116" cy="72" r="14" fill="#DDD6FE" stroke="#7C3AED" stroke-width="1.5"/>
        <circle cx="116" cy="71" r="6" fill="#ffffff" stroke="#7C3AED" stroke-width="1.3"/>
        <path d="M104 100 c0-9 6-15 12-15h0c6 0 12 6 12 15" fill="#DDD6FE" stroke="#7C3AED" stroke-width="1.5" stroke-linecap="round"/>
        <circle cx="148" cy="128" r="16" fill="#7C3AED" opacity="0.10"/>
        <circle cx="148" cy="128" r="12" fill="#ffffff" stroke="#7C3AED" stroke-width="2"/>
        <line x1="148" y1="122" x2="148" y2="128" stroke="#7C3AED" stroke-width="2" stroke-linecap="round"/>
        <line x1="148" y1="131" x2="148" y2="133" stroke="#7C3AED" stroke-width="2" stroke-linecap="round"/>
        <circle cx="50" cy="52" r="3" fill="#A78BFA"/>
        <circle cx="156" cy="56" r="2" fill="#C4B5FD"/>
      </svg>
      <h3>No Talent Pool Yet</h3>
      <p>Matching candidates for this job will appear here.</p>
    </div>
  </ng-container>

  <!-- ==================== V2 Design (existing) ==================== -->
  <ng-container *ngIf="designVersion !== 'v3'">

  <!-- <app-candidate-card-table-view
    [pageName]="'talent-pool'"
    [response]="response"
    [loadAPIcall]="loadAPIcall"
    [stopscrollFlag]="stopscrollFlag"
    [responseReceived]="responseReceived"
    [jobDetails]="jobDetails"
    [userId]="userId"
    [showTimeline]="false"
    (onCandidateClick)="route($event)"
    (onViewProfile)="open($event)"
    (onDownloadResume)="downloadResume($event)"
    (onSendMessage)="sendMessage($event)"
    (onOpenDocuments)="openDocuments($event)"
    (onShareProfile)="shareProfile($event)"
    (onScheduleInterview)="scheduleInterview($event)"
    (onAddNotes)="addNotes($event)"
    (onStatusUpdate)="updateStatus($event.status, $event.candidate)"
    (showDetails)="showDetails($event,DetailsPopup)"
    (inviteClicked)="invite($event)">
  </app-candidate-card-table-view> -->

  <!-- Table Structure using candidate-card with row-design view -->
  <!-- Job-level disable: blocked / non-active job greys the V2 Talent Pool
       listing and blocks pointer events. -->
  <div class="talent-pool-table-wrapper"
    [style.height]="'calc(100vh - 198px)'"
    infiniteScroll [infiniteScrollDistance]="5" [scrollWindow]="false"
    [infiniteScrollThrottle]="200" [alwaysCallback]="true"
    [ngClass]="{'disabled': !jobDetails?.availability || jobDetails?.status !== 'ACTIVE'}"
    (scrolled)="onScrollDown()">
    <div class="talent-pool-table-container hpanel">

      <!-- Table Header -->
      <div class="talent-pool-table-header panel-body">
        <div class="header-cell rank-col">Rank</div>
        <div class="header-cell candidate-col">Candidate</div>
        <div class="header-cell location-col">Title & Location</div>
        <div class="header-cell skills-col">Skills</div>
        <div class="header-cell match-col">Match %</div>
        <div class="header-cell engagement-col">Engagement</div>
        <div class="header-cell actions-col">Quick Actions</div>
      </div>

      <!-- Loading Skeleton -->
      <div class="col-12" *ngIf="loadAPIcall && response?.length==0">
        <app-common-skeleton [countforView]="4" [NameOftheScreen]="'jobcardlarge'"></app-common-skeleton>
      </div>

      <!-- Candidate Rows using candidate-card with row-design view -->
      <ng-container *ngIf="response.length > 0; else noResult">
        <div class="talent-pool-table-body">
          <ng-container *ngFor="let item of response; let i = index">
            <app-candidate-card
              [candidateval]="setRankAndShowSkills(item, i + 1)"
              [viewSize]="'row-design'"
              [pageName]="'talent-pool'"
              [appliedJobDetails]="jobDetails"
              [userId]="userId"
              [showStatus]="false"
              [showTimeline]="false">
            </app-candidate-card>
          </ng-container>
        </div>

        <!-- Bottom Reached Message -->
        <div *ngIf="stopscrollFlag && response.length > 0" class="caught_up">
          <div class="col-md-12">
            <ng-icon name="tablerCircleCheck"></ng-icon>
          </div>
          <div class="col-md-12 fig">You've reached the bottom of the page!</div>
        </div>
      </ng-container>

      <!-- No Result Template -->
      <ng-template #noResult>
        <div class="wert" *ngIf="!loadAPIcall || responseReceived">
          <div class="col-md-12 mg-bt-9">
            <ng-icon [name]="noResultSvgType"></ng-icon>
          </div>
          <div class="col-md-12 fig">{{noResultMessage}}</div>
        </div>
      </ng-template>

    </div>
  </div>

  <!-- <div class="talent-pool-widgets">
    <app-common-widget [page]="'GIG_JOB_LIST'"
      *ngIf="userType!='FREELANCE_RECRUITER' && userType!='MANAGEMENT_TALENT_ACQUISITION'"
      [showsuggJobsData]="showSuggJobsDataServer" [inputData]="'GIG_JOB_LIST'">
    </app-common-widget>

    <app-common-widget [page]="'GIG_JOB_LIST_BOTH'"
      *ngIf="userType=='FREELANCE_RECRUITER' || userType=='MANAGEMENT_TALENT_ACQUISITION'"
      [showsuggJobsData]="showSuggJobsDataServer" [inputData]="'GIG_JOB_LIST_BOTH'">
    </app-common-widget>
  </div> -->

  </ng-container>

  <ng-template #DetailsPopup let-modal>
    <div class="match-details-modal">
      <!-- Enhanced Header with Score Badge -->
      <div class="modal-header-enhanced">
        <div class="header-content">
          <div class="score-badge-container">
            <div class="score-badge">
              <span class="score-value">{{candidateScore}}</span>
              <span class="score-label">Match Score</span>
            </div>
          </div>
          <div class="header-text">
            <h3 class="modal-title">Candidate Match Analysis</h3>
            <p class="modal-subtitle">Detailed breakdown of compatibility factors</p>
          </div>
        </div>
        <button type="button" class="close-btn-modern" aria-label="Close" (click)="closeModal()">
          <ng-icon name="tablerX"></ng-icon>
        </button>
      </div>

      <!-- Modal Body with Enhanced Sections -->
      <div class="modal-body-enhanced">
        <div *ngFor="let r of reasons; let i = index" class="reason-card">

          <!-- Overall Reason Section -->
          <div class="reason-summary-section">
            <div class="section-icon reason-icon">
              <ng-icon name="tablerInfoCircle"></ng-icon>
            </div>
            <div class="section-content">
              <h4 class="section-title">Match Summary</h4>
              <p class="reason-summary-text">{{ r.reason }}</p>
            </div>
          </div>

          <!-- Strengths Section with Green Theme -->
          <div class="strengths-section">
            <div class="section-header">
              <div class="section-icon strengths-icon">
                <!-- <ng-icon name="tablerCircleCheck"></ng-icon> -->
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd" />
                      </svg>
              </div>
              <h4 class="section-title strengths-title">
                Strengths
                <span class="count-badge strengths-badge">{{ r.strengths?.length || 0 }}</span>
              </h4>
            </div>
            <div class="section-body">
              <div class="strength-item" *ngFor="let s of r.strengths; let idx = index">
                <div class="item-bullet strengths-bullet">
                  <!-- <ng-icon name="tablerCheck"></ng-icon> -->
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd" />
                  </svg>
                </div>
                <span class="item-text">{{ s }}</span>
              </div>
              <div *ngIf="!r.strengths || r.strengths.length === 0" class="empty-state">
                <ng-icon name="tablerInfoCircle"></ng-icon>
                <span>No specific strengths identified</span>
              </div>
            </div>
          </div>

          <!-- Weaknesses Section with Red Theme -->
          <div class="weaknesses-section">
            <div class="section-header">
              <div class="section-icon weaknesses-icon">
                <!-- <ng-icon name="tablerAlertTriangle"></ng-icon> -->
                <ng-icon name="tablerAlertTriangle"></ng-icon>
              </div>
              <h4 class="section-title weaknesses-title">
                Areas for Improvement
                <span class="count-badge weaknesses-badge">{{ r.weaknesses?.length || 0 }}</span>
              </h4>
            </div>
            <div class="section-body">
              <div class="weakness-item" *ngFor="let w of r.weaknesses; let idx = index">
                <div class="item-bullet weaknesses-bullet">
                  <!-- <ng-icon name="tablerCircleMinus"></ng-icon> -->
                  <ng-icon name="tablerAlertCircle"></ng-icon>
                </div>
                <span class="item-text">{{ w }}</span>
              </div>
              <div *ngIf="!r.weaknesses || r.weaknesses.length === 0" class="empty-state">
                <ng-icon name="tablerThumbUp"></ng-icon>
                <span>No significant gaps identified</span>
              </div>
            </div>
          </div>

          
        </div>

        <!-- Empty State if No Reasons -->
        <div *ngIf="!reasons || reasons.length === 0" class="no-data-state">
          <ng-icon name="tablerSearch"></ng-icon>
          <p>No match analysis available for this candidate.</p>
        </div>
      </div>

      <!-- Enhanced Footer -->
      <div class="modal-footer-enhanced">
        <button class="btn-close-modern" (click)="closeModal()">
          Close
        </button>
      </div>
    </div>
  </ng-template>

</div>

<ng-template #jobsupplytemplate>
  <app-new-job [focus]="focus" [screenName]="'invite-job-listing'" [filterData]="candidateData"></app-new-job>
</ng-template>
